import type { ReactNode } from "react"
import { useTranslation } from "react-i18next"

import { Alert, AlertDescription } from "@/components/ui/alert"
import { useOnline } from "@/hooks/use-online"
import { cn } from "@/lib/utils"

interface ListShellProps {
  /** Page heading rendered at the top of the shell. */
  title: string
  /** Optional one-line subtitle / total-count caption rendered under the title. */
  subtitle?: ReactNode
  /** Toolbar slot — rendered above the body. Typically a `<ListToolbar>`. */
  toolbar?: ReactNode
  /** Primary action slot — rendered to the right of the title row (e.g. "Add handle"). */
  action?: ReactNode
  /** Body slot — typically a `<Table>`, an `EmptyState`, or a state component. */
  children: ReactNode
  /** Footer slot — typically a `<ListPagination>`. */
  footer?: ReactNode
  /**
   * `data-testid` for the page-level container. Pages pass their list
   * identifier here (e.g. `admin-brands-list`) — assertions and
   * Playwright fixtures key off this attribute.
   */
  testId?: string
  className?: string
}

/**
 * ListShell — shared page-level chrome for the four admin list pages
 * (Brands / Agencies / Creators / Blocklist).
 *
 * Composes:
 *   - title + optional subtitle row, with an optional primary action on the right
 *   - offline banner (driven by `useOnline`) — surfaces above the toolbar when
 *     the browser reports offline; matches UX Spec §4 — search/filter
 *     disabled with tooltip handled by the consuming page
 *   - toolbar slot (search, filters, sort)
 *   - body slot — caller renders the table or 5-state component
 *   - footer slot — typically `<ListPagination>`
 *
 * The shell itself owns NO state — pages drive filters / pagination via
 * URL params + Inertia `router.get` partial reload (per OKS-15 TRD v6 §State).
 *
 * Test IDs:
 *   - The container exposes the caller-provided `testId` (page-specific).
 *   - The offline banner is `admin-offline-banner` per the locked v6 testid
 *     contract — single `data-testid` shared across all admin list pages.
 */
export function ListShell({
  title,
  subtitle,
  toolbar,
  action,
  children,
  footer,
  testId,
  className,
}: ListShellProps) {
  const { t } = useTranslation()
  const online = useOnline()

  return (
    <div
      className={cn("flex flex-col gap-6 p-6", className)}
      data-testid={testId}
    >
      <header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-1">
          <h1 className="text-foreground text-2xl font-semibold tracking-tight">
            {title}
          </h1>
          {subtitle && (
            <div className="text-muted-foreground text-sm">{subtitle}</div>
          )}
        </div>
        {action && <div className="flex flex-shrink-0">{action}</div>}
      </header>

      {!online && (
        <Alert data-testid="admin-offline-banner">
          <AlertDescription>
            {t("admin.shared.offline_banner")}
          </AlertDescription>
        </Alert>
      )}

      {toolbar}

      <div>{children}</div>

      {footer}
    </div>
  )
}
